import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Send, ArrowLeft, Lock, MessageCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";

const MessageAdmin = () => {
  const { token, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    loadThread();
  }, [isAuthenticated]);

  const loadThread = async () => {
    try {
      const res = await axios.get(`${API}/inmail/admin/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThread(res.data || []);
    } catch (e) {
      // silent — not critical
    } finally {
      setLoadingThread(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const trimmed = text.trim();
    if (!trimmed) { toast.error("Please write a message"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/inmail/admin`, {
        subject: subject.trim() || undefined,
        text: trimmed,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Sent securely to Admin. You'll be notified when they reply.");
      setSubject("");
      setText("");
      loadThread();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="message-admin-page">
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#8A92A0] hover:text-white text-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <div className="flex items-center gap-2 text-xs text-[#8A92A0]">
            <Lock className="w-3.5 h-3.5 text-[#EC008C]" />
            <span>Secure • In-house • Encrypted at rest</span>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Hero */}
          <div className="mb-8 flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#EC008C]/15 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6 text-[#EC008C]" />
            </div>
            <div>
              <h1 className="font-['Unbounded'] text-2xl sm:text-3xl font-bold text-white mb-1">Message Admin</h1>
              <p className="text-[#8A92A0] text-sm">
                Send a secure in-mail directly to the StardomFans admin team. Stored inside our platform only — never routed through third-party email.
              </p>
            </div>
          </div>

          {/* Compose */}
          <form onSubmit={handleSend} className="card p-6 mb-8" data-testid="admin-inmail-form">
            <label className="block text-xs font-semibold text-[#8A92A0] uppercase tracking-wide mb-2">Subject (optional)</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Payout question, Product upload issue, Store activation..."
              maxLength={120}
              className="bg-[#050810] border-[#1A2030] text-white mb-4"
              data-testid="admin-inmail-subject"
            />
            <label className="block text-xs font-semibold text-[#8A92A0] uppercase tracking-wide mb-2">Message</label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your message to Admin. Be as detailed as you'd like — we're listening."
              maxLength={4000}
              rows={6}
              className="bg-[#050810] border-[#1A2030] text-white resize-none"
              data-testid="admin-inmail-textarea"
              required
            />
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-[#4A5260]">{text.length} / 4000</span>
              <Button
                type="submit"
                disabled={sending || !text.trim()}
                className="bg-[#EC008C] text-black hover:bg-[#FF3399] rounded-full px-6"
                data-testid="admin-inmail-send-btn"
              >
                {sending ? "Sending..." : <><Send className="w-4 h-4 mr-1.5" /> Send Securely</>}
              </Button>
            </div>
          </form>

          {/* Thread history */}
          <div>
            <h2 className="font-['Unbounded'] text-lg font-bold text-white mb-4 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-[#EC008C]" /> Your Conversation with Admin
            </h2>
            {loadingThread ? (
              <div className="card p-8 text-center text-[#8A92A0] text-sm">Loading...</div>
            ) : thread.length === 0 ? (
              <div className="card p-8 text-center text-[#8A92A0] text-sm" data-testid="admin-inmail-empty">
                No previous messages. Send your first message above.
              </div>
            ) : (
              <div className="space-y-4" data-testid="admin-inmail-thread">
                {thread.map((m) => (
                  <div key={m.inmail_id} className="card p-5" data-testid={`inmail-${m.inmail_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-white text-sm">{m.subject || "In-mail to Admin"}</span>
                      <span className="text-xs text-[#4A5260] flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDate(m.created_at)}
                      </span>
                    </div>
                    <div className="mb-3 pl-3 border-l-2 border-[#1A2030]">
                      <p className="text-xs text-[#8A92A0] mb-1">You wrote:</p>
                      <p className="text-sm text-white whitespace-pre-wrap break-words">{m.text}</p>
                    </div>
                    {m.admin_reply ? (
                      <div className="mt-3 p-3 rounded-xl bg-[#EC008C]/5 border border-[#EC008C]/20">
                        <p className="text-xs text-[#EC008C] mb-1 flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" /> Admin replied · {m.admin_replied_at ? formatDate(m.admin_replied_at) : ""}
                        </p>
                        <p className="text-sm text-white whitespace-pre-wrap break-words">{m.admin_reply}</p>
                      </div>
                    ) : (
                      <p className="text-xs text-[#4A5260] italic">Awaiting admin reply — you&apos;ll be notified in-app.</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MessageAdmin;
